// Client-side mirror of internal/analysis/bars.go IsMarketOpen so the
// UI can flip labels and countdowns based on US equity market state
// without waiting for a server round-trip. Uses Intl.DateTimeFormat
// with `timeZone: "America/New_York"` so a user in any local zone
// sees the authoritative ET wall-clock.
//
// No holiday modeling — matches the Go side exactly. If the IANA TZ
// database isn't available for some reason (very old runtime), the
// helpers fall back to "market open" so the UI never silently pretends
// it's Saturday.

interface ETParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number; // 0 = Sun, 1 = Mon, …, 6 = Sat
}

const etFormatter = (() => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      weekday: "short",
      hour12: false,
    });
  } catch {
    return null;
  }
})();

const weekdayMap: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function etParts(d: Date): ETParts | null {
  if (!etFormatter) return null;
  const parts = etFormatter.formatToParts(d);
  const m: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== "literal") m[p.type] = p.value;
  }
  // Some runtimes (older Node) emit "24" for the midnight hour under
  // hour12:false — normalize to 0 so the minute arithmetic stays sane.
  const hourRaw = Number(m.hour);
  const hour = Number.isFinite(hourRaw) ? hourRaw % 24 : NaN;
  return {
    year: Number(m.year),
    month: Number(m.month),
    day: Number(m.day),
    hour,
    minute: Number(m.minute),
    weekday: weekdayMap[m.weekday ?? ""] ?? -1,
  };
}

/**
 * Reports whether the US equity regular trading session is currently
 * open: Monday–Friday, 9:30:00 ≤ ET wall-clock < 16:00:00. Holidays
 * are not modeled. Accepts an optional clock argument so tests can
 * drive it with a fixed moment; defaults to `new Date()`.
 */
export function isMarketOpen(now: Date = new Date()): boolean {
  const p = etParts(now);
  if (!p) return true;
  if (p.weekday < 1 || p.weekday > 5) return false;
  const mins = p.hour * 60 + p.minute;
  return mins >= 9 * 60 + 30 && mins < 16 * 60;
}
